import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import AuthStore from '@app/src/common/store/AuthStore';
import { EXTERNAL_PROVIDER_CALLBACK_URL } from '@app/src/common/constants/common';
import ROUTES from '@app/src/common/constants/routeUrls';
import { needsAuthRedirect } from '@app/src/common/services/authRouteAccess';
import {
  buildCallbackErrorMessage,
  buildErrorPageUrl
} from '@app/src/common/services/authCallbackError';

import { Spinner, SpinnerModifiers } from '@fw-nextgen/fw-components';
import AppStore from '@app/src/common/store/AppStore';

// Pages that render without a session. Handing the browser to the identity provider from one of them
// is what produced the SSO callback loop: a failed /callback lands on /#/not-authorized — a full
// document load, since the pathname changes — this effect re-runs, checkAccess finds no token and
// redirects to /auth/auth, and the provider's still-live SSO cookie answers with a fresh code, so the
// two redirects alternate forever. Home and Consent both need a session and stay out of the list.
const SESSION_FREE_PATHS = [
  ROUTES.authPage.path,
  ROUTES.resetPasswordPage.path,
  ROUTES.notAuthorizedPage.path,
  ROUTES.forgotPasswordPage.path,
  ROUTES.logOutPage.path,
  ROUTES.serverErrorPage.path,
  ROUTES.notFoundPage.path
];

const AuthProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const [init, setInit] = useState(false);

  useEffect(() => {
    if (window.location.pathname === EXTERNAL_PROVIDER_CALLBACK_URL) {
      const params = new URLSearchParams(window.location.hash);
      const code = params.get('code') ?? '';
      AuthStore.getTokenFromCode(code)
        .then(() => (window.location.href = '/#/'))
        .catch(error => {
          // The exchange is the only place the provider's reason for refusing the login is
          // available, and no toast can render here (ToastContainer lives inside these children),
          // so it is handed to the error page as a query parameter.
          const message = buildCallbackErrorMessage(error);
          window.location.href = buildErrorPageUrl(
            ROUTES.notAuthorizedPage.path,
            message
          );
        });
    } else {
      const isResetPage = location.hash.includes(ROUTES.resetPasswordPage.path);
      if (isResetPage) {
        setInit(true);
      } else {
        AuthStore.checkAccess(
          needsAuthRedirect(location.hash, SESSION_FREE_PATHS)
        )
          .then(() => AuthStore.initCurrentUser())
          .then(() => AppStore.defineUser())
          .then(() => setInit(true))
          .catch(reason => setInit(!reason));
      }
    }
  }, []);

  return init ? (
    <>{children}</>
  ) : (
    <Spinner text={''} modifiers={[SpinnerModifiers.FULLSIZE]} />
  );
};

export default observer(AuthProvider);
